import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Midi } from '@tonejs/midi';
import { DrumMachine, Soundfont } from 'smplr';
import * as Tone from 'tone';

import {
  DrumPlayer,
  NotePlayer,
  PERCUSSION_CHANNEL,
  createFallbackDrums,
  createFallbackSynth,
  getGmInstrumentName,
  getPercussionGroupName,
} from './midi.constants';
import { MidiEngine } from './playbackEngine.types';

const EMPTY_DENSITY: number[] = [];
const NOTE_DENSITY_BUCKETS = 150;

function computeNoteDensity(midi: Midi): number[] {
  const { duration } = midi;
  if (duration <= 0) return [];

  const buckets = new Array(NOTE_DENSITY_BUCKETS).fill(0);
  const bucketWidth = duration / NOTE_DENSITY_BUCKETS;

  midi.tracks.forEach((track) => {
    track.notes.forEach((note) => {
      const startBucket = Math.floor(note.time / bucketWidth);
      const endBucket = Math.floor((note.time + note.duration) / bucketWidth);
      for (
        let i = Math.max(0, startBucket);
        i <= Math.min(endBucket, NOTE_DENSITY_BUCKETS - 1);
        i += 1
      ) {
        buckets[i] += note.velocity;
      }
    });
  });

  const max = Math.max(...buckets, 1);
  return buckets.map((v) => v / max);
}

function wrapSoundfont(sf: Soundfont): NotePlayer {
  return {
    start: (note, time, duration, velocity) => {
      sf.start({ note, time, duration, velocity });
    },
    stop: () => sf.stop(),
    dispose: () => sf.stop(),
  };
}

function wrapDrumMachine(dm: DrumMachine): DrumPlayer {
  return {
    hit: (midiNote, time, velocity) => {
      dm.start({ note: getPercussionGroupName(midiNote), time, velocity });
    },
    stop: () => dm.stop(),
    dispose: () => dm.stop(),
  };
}

export const useMidiEngine = (): MidiEngine => {
  const instrumentsRef = useRef<Map<string, NotePlayer>>(new Map());
  const drumsRef = useRef<DrumPlayer | null>(null);
  const midiRef = useRef<Midi | null>(null);
  const scheduledEventsRef = useRef<number[]>([]);
  const durationRef = useRef(0);
  const noteDensityRef = useRef<number[]>(EMPTY_DENSITY);

  const stopAllInstruments = useCallback(() => {
    instrumentsRef.current.forEach((inst) => inst.stop());
    drumsRef.current?.stop();
  }, []);

  const cleanup = useCallback(() => {
    scheduledEventsRef.current.forEach((id) => Tone.Transport.clear(id));
    scheduledEventsRef.current = [];
    Tone.Transport.stop();
    Tone.Transport.position = 0;
    stopAllInstruments();
  }, [stopAllInstruments]);

  const scheduleNotes = useCallback(() => {
    if (!midiRef.current) return;

    scheduledEventsRef.current.forEach((id) => Tone.Transport.clear(id));
    scheduledEventsRef.current = [];

    midiRef.current.tracks.forEach((track) => {
      if (track.notes.length === 0) return;

      if (track.channel === PERCUSSION_CHANNEL) {
        const drums = drumsRef.current;
        if (!drums) return;
        track.notes.forEach((note) => {
          const id = Tone.Transport.schedule((time) => {
            drums.hit(note.midi, time, Math.round(note.velocity * 127));
          }, note.time);
          scheduledEventsRef.current.push(id);
        });
      } else {
        const name = getGmInstrumentName(track.instrument.number);
        const instrument = instrumentsRef.current.get(name);
        if (!instrument) return;
        track.notes.forEach((note) => {
          const id = Tone.Transport.schedule((time) => {
            instrument.start(
              note.midi,
              time,
              note.duration,
              Math.round(note.velocity * 127)
            );
          }, note.time);
          scheduledEventsRef.current.push(id);
        });
      }
    });
  }, []);

  const loadMidi = useCallback(
    async (url: string): Promise<number> => {
      cleanup();

      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const midi = new Midi(arrayBuffer);
      midiRef.current = midi;
      durationRef.current = midi.duration;
      noteDensityRef.current = computeNoteDensity(midi);

      const ctx = Tone.getContext().rawContext as AudioContext;

      const neededInstruments = new Set<string>();
      let needsDrums = false;

      midi.tracks.forEach((track) => {
        if (track.notes.length === 0) return;
        if (track.channel === PERCUSSION_CHANNEL) {
          needsDrums = true;
        } else {
          neededInstruments.add(getGmInstrumentName(track.instrument.number));
        }
      });

      if (needsDrums && !drumsRef.current) {
        try {
          const dm = new DrumMachine(ctx, { instrument: 'TR-808' });
          await dm.load;
          drumsRef.current = wrapDrumMachine(dm);
        } catch {
          drumsRef.current = createFallbackDrums();
        }
      }

      const loadPromises: Promise<void>[] = [];
      Array.from(neededInstruments).forEach((name) => {
        if (instrumentsRef.current.has(name)) return;
        const promise = (async () => {
          try {
            const sf = new Soundfont(ctx, { instrument: name as any });
            await sf.load;
            instrumentsRef.current.set(name, wrapSoundfont(sf));
          } catch {
            instrumentsRef.current.set(name, createFallbackSynth());
          }
        })();
        loadPromises.push(promise);
      });
      await Promise.all(loadPromises);

      Tone.Transport.cancel();
      scheduleNotes();

      const stopId = Tone.Transport.schedule(() => {
        Tone.Transport.pause();
      }, midi.duration);
      scheduledEventsRef.current.push(stopId);

      return midi.duration;
    },
    [cleanup, scheduleNotes]
  );

  const play = useCallback(async () => {
    if (Tone.context.state !== 'running') {
      await Tone.start();
    }
    Tone.Transport.start();
  }, []);

  const pause = useCallback(() => {
    Tone.Transport.pause();
    stopAllInstruments();
  }, [stopAllInstruments]);

  const stop = useCallback(() => {
    Tone.Transport.stop();
    Tone.Transport.position = 0;
    stopAllInstruments();
  }, [stopAllInstruments]);

  const seekTo = useCallback(
    (progress: number) => {
      const wasPlaying = Tone.Transport.state === 'started';
      Tone.Transport.pause();
      stopAllInstruments();

      const targetTime = progress * durationRef.current;
      Tone.Transport.seconds = targetTime;

      if (wasPlaying) {
        Tone.Transport.start();
      }
    },
    [stopAllInstruments]
  );

  const getDuration = useCallback(() => durationRef.current, []);
  const getCurrentTime = useCallback(() => Tone.Transport.seconds, []);
  const getNoteDensity = useCallback(() => noteDensityRef.current, []);

  const dispose = useCallback(() => {
    cleanup();
    instrumentsRef.current.forEach((inst) => inst.dispose());
    instrumentsRef.current.clear();
    drumsRef.current?.dispose();
    drumsRef.current = null;
    midiRef.current = null;
    durationRef.current = 0;
    noteDensityRef.current = EMPTY_DENSITY;
  }, [cleanup]);

  useEffect(() => {
    return () => {
      dispose();
    };
  }, [dispose]);

  return useMemo(
    () => ({
      type: 'midi' as const,
      loadMidi,
      play,
      pause,
      stop,
      seekTo,
      getDuration,
      getCurrentTime,
      getNoteDensity,
    }),
    [
      loadMidi,
      play,
      pause,
      stop,
      seekTo,
      getDuration,
      getCurrentTime,
      getNoteDensity,
    ]
  );
};
